import { Component, DestroyRef, OnInit } from '@angular/core';
import { catchError, EMPTY, map, Observable, of, switchMap, tap } from 'rxjs';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { OrganizationService } from '@markant-platform/shared/organization/data-access';
import { AuthenticatedUserService, UserService } from '@markant-platform/shared/user/data-access';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import {
  MarkantSelectOption,
  NO_VALUE_OPTION,
  notBlank,
  RegexPattern,
  removeWhitespace,
  validateGLNCheckDigit,
  validateIBAN,
  validateVatId,
} from '@markant-platform/shared/util-helpers';
import { SnackbarService } from '@markant-platform/shared/snackbar/feature';
import { StaticRole } from '@markant-platform/shared/user/model';
import { OrganizationDocumentService } from '@markant-platform/shared/organization-document/data-access';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AddressFormGroup } from '@markant-platform/shared/form';
import { CommonModule } from '@angular/common';
import { UiCountrySelectComponent, UiPageHeaderComponent } from '@markant-platform/shared/ui-components';
import { MarkantElementsModule } from '@markant/elements-angular';
import { UiCardComponent } from '@markant-platform/shared/ui-card';
import { FormControlErrorPipe, FormControlKeyErrorPipe } from '@markant-platform/shared/util-pipes';
import { ModalBridgeComponent } from '@markant-platform/shared/ui-modal';
import { AuditReasonDialogComponent } from '@markant-platform/shared/dialog/feature';
import { Industry, OrganizationDetailData, OrganizationStatus, OrganizationType, QualificationLevel } from 'data-model';

@Component({
  selector: 'markant-platform-organization-details-form',
  templateUrl: './organization-details-form.component.html',
  styleUrls: ['./organization-details-form.component.scss', '../shared-organization.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    UiPageHeaderComponent,
    MarkantElementsModule,
    UiCardComponent,
    TranslocoPipe,
    FormControlErrorPipe,
    FormControlKeyErrorPipe,
    RouterLink,
    ModalBridgeComponent,
    UiCountrySelectComponent,
    AuditReasonDialogComponent,
  ],
})
export class OrganizationDetailsFormComponent implements OnInit {
  isLoading = true;
  showError = false;
  organizationId?: string;
  isCurrentUserPlatformAdmin = false;
  isCurrentUserOA = false;
  userRoles?: string[];
  userCount = 0;
  documentCount = 0;
  isDemoOrganization = false;
  isAuditReasonDialogOpen = false;

  OrganizationStatus = OrganizationStatus;
  organizationDetailData: OrganizationDetailData | undefined;
  isEzrCustomer = false;

  formBuilder = new FormBuilder();
  openApprovalDialog = false;
  masterKinChanged = false;
  openMarkantOrganizationChangeDialog = false;
  auditReason: string | undefined;

  addressFormGroup = new AddressFormGroup();
  paymentInformationFormGroup = this.formBuilder.group({
    iban: ['', validateIBAN],
    bic: ['', Validators.pattern(RegexPattern.BIC_PATTERN)],
    bank: '',
  });
  organizationForm = this.formBuilder.group({
    id: [{ value: '', disabled: true }],
    name: [{ value: '', disabled: true }, [Validators.required, Validators.maxLength(128), notBlank]],
    gln: [{ value: '', disabled: true }, [Validators.pattern(RegexPattern.GLN_PATTERN), validateGLNCheckDigit]],
    phone: ['', [Validators.pattern(RegexPattern.PHONE_PATTERN)]],
    salesTaxId: [{ value: '', disabled: true }, this.validateVatIdBasedOnQualificationLevel.bind(this)],
    salesTaxNotAvailable: { value: false, disabled: true },
    taxNumber: { value: '', disabled: true },
    commercialRegisterId: '',
    commercialRegister: '',
    qualificationLevel: { value: '', disabled: true },
    naceCode: '',
    organizationType: [{ value: '', disabled: true }, []],
    industry: [{ value: '', disabled: true }],
    masterKin: [{ value: '', disabled: true }, [Validators.pattern(RegexPattern.MASTER_KIN)]],
    furtherKins: this.formBuilder.array([]),
    crmId: [{ value: '', disabled: true }],
    markantId: ['', [Validators.pattern(RegexPattern.MARKANT_ID_PATTERN)]],
    markantOrganization: [{ value: false, disabled: true }],
    customerNumber: [{ value: '', disabled: true }],
    billingLanguageIsoCode: '',
    address: this.addressFormGroup,
    paymentInformation: this.paymentInformationFormGroup,
    billingEmail: [
      { value: '', disabled: true },
      [Validators.pattern(RegexPattern.BILLING_EMAIL_PATTERN), Validators.pattern(RegexPattern.XSS_PATTERN)],
    ],
  });

  private validateVatIdBasedOnQualificationLevel(control: FormControl): ValidationErrors | null {
    if (this.isDemoOrganization && (!control.value || control.value?.length == 0)) {
      return null;
    }
    return validateVatId(control);
  }

  protected readonly Industry = Industry;
  protected readonly QualificationLevel = QualificationLevel;
  private readonly errorMessagesContactSupport = [
    ['updateOrganization.data.name'],
    ['GLN', 'is duplicated'],
    ['Sales tax or tax number required. Only one value must be set at a time! (XOR)'],
    ['Value does not match supported VATID format'],
  ];

  protected validIsoCodeOptions = this.getValidIsoCodeOptions();
  protected industryOptions = this.getIndustryOptions();
  protected organizationTypeOptions = this.getOrganizationTypeOptions();

  constructor(
    public readonly translocoService: TranslocoService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly organizationService: OrganizationService,
    private readonly snackbarService: SnackbarService,
    private readonly authenticatedUserService: AuthenticatedUserService,
    private readonly userService: UserService,
    private readonly documentService: OrganizationDocumentService,
    private readonly destroyRef: DestroyRef
  ) {
    this.translocoService.langChanges$.pipe(takeUntilDestroyed()).subscribe(() => {
      this.validIsoCodeOptions = this.getValidIsoCodeOptions();
      this.industryOptions = this.getIndustryOptions();
      this.organizationTypeOptions = this.getOrganizationTypeOptions();
    });
  }

  private getValidIsoCodeOptions(): MarkantSelectOption[] {
    return [
      {
        value: NO_VALUE_OPTION,
        representation: this.translocoService.translate('organization.details.fields.billingLanguageIsoCode.null'),
      },
      {
        value: 'en-GB',
        representation: this.translocoService.translate('languageSelect.en-gb'),
      },
      {
        value: 'de-DE',
        representation: this.translocoService.translate('languageSelect.de-de'),
      },
    ];
  }

  private getIndustryOptions(): MarkantSelectOption[] {
    return Object.keys(Industry).map((key: string) => ({
      value: Industry[key as Industry],
      representation: this.translocoService.translate(
        `organization.details.fields.industry.${Industry[key as Industry]}`
      ),
    }));
  }

  private getOrganizationTypeOptions(): MarkantSelectOption[] {
    return Object.keys(OrganizationType).map((key: string) => ({
      value: OrganizationType[key as OrganizationType],
      representation: this.translocoService.translate(
        `organization.details.fields.organizationType.options.${OrganizationType[key as OrganizationType].toLowerCase()}`
      ),
    }));
  }

  get furtherKins(): FormArray {
    return this.organizationForm.controls['furtherKins'] as FormArray;
  }

  ngOnInit(): void {
    this.getOrganizationId()
      .pipe(
        tap(organizationId => (this.organizationId = organizationId)),
        tap(organizationId =>
          this.userService
            .getCountOfUsersInOrganization(organizationId)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(userCount => (this.userCount = userCount))
        ),
        tap(organizationId =>
          this.documentService
            .getDocumentsCountForOrganization(organizationId)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(documentCount => (this.documentCount = documentCount))
        ),
        switchMap(() => this.getRequesterRoles()),
        tap(roles => {
          this.userRoles = roles;
          this.isCurrentUserPlatformAdmin = roles?.some(role => role === StaticRole.PLATFORM_ADMIN);
          this.isCurrentUserOA = roles?.some(role => role === StaticRole.ORGANIZATION_ADMIN);
        }),
        catchError(() => {
          this.snackbarService.showErrorSnackbarWithTransloco('common.insufficientRolesError');
          return EMPTY;
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.loadData());
  }

  loadData(): void {
    this.isLoading = true;
    if (!this.organizationId || !this.userRoles || this.organizationId.trim().length === 0) {
      this.snackbarService.showErrorSnackbarWithTransloco('common.insufficientRolesError');
      return;
    }
    if (
      !this.userRoles.includes(StaticRole.PLATFORM_ADMIN) &&
      !this.userRoles.includes(StaticRole.ORGANIZATION_ADMIN) &&
      !this.userRoles.includes(StaticRole.ORGANIZATION_MANAGER)
    ) {
      this.snackbarService.showErrorSnackbarWithTransloco('common.insufficientRolesError');
      return;
    }

    this.organizationService
      .getOrganizationDetailsById(this.organizationId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: masterData => {
          this.organizationDetailData = masterData;
          this.isDemoOrganization = masterData.qualificationLevel === QualificationLevel.LEVEL_1;
          this.fillForm(masterData);
          this.activateForm();
          this.isLoading = false;
        },
        error: error => {
          this.handleError(error, 'organization.details.loadingError');
          this.isLoading = false;
        },
      });
  }

  fillForm(details: OrganizationDetailData): void {
    this.isEzrCustomer = details.ezrCustomer;
    this.organizationForm.patchValue({
      ...details,
      salesTaxNotAvailable: details.salesTaxId ? details.salesTaxId.trim().length === 0 : true,
      customerNumber: '' + details.customerNumber,
    });

    for (let i = 0; i < this.furtherKins.length; i++) {
      this.removeFurtherKin(i);
    }
    details.furtherKins?.forEach(furtherKin => {
      this.addFurtherKin(furtherKin);
    });

    if (!details.billingLanguageIsoCode) {
      this.organizationForm.controls.billingLanguageIsoCode.setValue(NO_VALUE_OPTION);
    }

    this.updateRequiredFields(details);
  }

  closeDialog(): void {
    this.openApprovalDialog = false;
  }

  onMasterKinChange(value: string): void {
    this.masterKinChanged = this.organizationDetailData?.masterKin !== value && !!value;
  }

  approveMasterKinChanges(): void {
    this.masterKinChanged = false;
    this.openApprovalDialog = false;
    this.onSubmit();
  }

  onSubmit(): void {
    if (this.organizationForm.get('masterKin')?.valid && this.masterKinChanged) {
      this.openApprovalDialog = true;
      return;
    }

    if (this.organizationForm.invalid || this.addressFormGroup.invalid || this.paymentInformationFormGroup.invalid) {
      this.showError = true;
      return;
    }

    if ((this.isCurrentUserPlatformAdmin || this.isCurrentUserOA) && !this.auditReason) {
      this.isAuditReasonDialogOpen = true;
      return;
    }

    this.isLoading = true;
    const newDetails = this.createDetailsUpdate(this.organizationForm);
    if (newDetails.billingLanguageIsoCode === NO_VALUE_OPTION) {
      newDetails.billingLanguageIsoCode = '';
    }

    this.organizationService
      .setOrganizationDetailsById(
        this.organizationId!,
        this.organizationDetailData as OrganizationDetailData,
        newDetails,
        this.auditReason
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.isLoading = false;
          this.router.navigate(['..'], { relativeTo: this.route });
          this.snackbarService.showSuccessSnackbarWithTransloco('common.saveSuccessful');
          return EMPTY;
        },
        error: (error: HttpErrorResponse) => {
          this.handleSubmitError(error);
        },
      });
  }

  createDetailsUpdate(organizationForm: FormGroup): OrganizationDetailData {
    const organizationDetailData = {
      ...this.organizationDetailData,
      ...organizationForm.value,
      name: organizationForm.controls['name'].value,
      salesTaxId: organizationForm.controls['salesTaxNotAvailable'].value
        ? null
        : organizationForm.controls['salesTaxId'].value,
      taxNumber: organizationForm.controls['salesTaxNotAvailable'].value
        ? organizationForm.controls['taxNumber'].value
        : null,
      gln: organizationForm.controls['gln'].value,
      masterKin: this.organizationDetailData?.masterKin,
      phone: removeWhitespace(organizationForm.controls['phone'].value),
      furtherKins: this.organizationDetailData?.furtherKins,
      crmId: this.organizationDetailData?.crmId,
      customerNumber: this.organizationDetailData?.customerNumber,
      organizationType: organizationForm.controls['organizationType'].value,
      industry: organizationForm.controls['industry'].value,
      billingEmail: organizationForm.controls['billingEmail'].value,
      version: this.organizationDetailData?.version,
      salesTaxNotAvailable: undefined,
    };

    if (this.isCurrentUserPlatformAdmin) {
      organizationDetailData.masterKin = this.organizationForm.controls.masterKin.value;
      organizationDetailData.furtherKins = (this.organizationForm.controls['furtherKins'] as FormArray).controls.map(
        control => control.value['kin']
      );
      organizationDetailData.crmId = this.organizationForm.controls.crmId.value;
      organizationDetailData.gln ||= '';
    }
    return organizationDetailData;
  }

  handleSubmitError(response: HttpErrorResponse): Observable<never> {
    if (response.error?.errorCode === 'ORG-107') {
      this.organizationForm.controls.gln.setErrors({ duplicated: true });
    }

    if (
      !this.isCurrentUserPlatformAdmin &&
      response.status === 400 &&
      this.errorMessagesContactSupport.some(v => v.every(message => response.error?.message?.includes(message)))
    ) {
      return this.handleError(response, 'organization.details.saveErrorContactSupport');
    } else if (
      response.error?.errorCode &&
      response.error.errorCode !== 'ORG-999' &&
      this.translocoService.translate('organization.details.error.' + response.error.errorCode) !==
        'organization.details.error.' + response.error.errorCode
    ) {
      this.snackbarService.showErrorSnackbarWithTransloco('organization.details.error.' + response.error.errorCode);
    } else if (response.status === 400 && response.error?.message?.includes('address')) {
      for (const addressControlName in this.addressFormGroup.controls) {
        if (new RegExp(addressControlName + ':').test(response.error?.message)) {
          const addressControl = this.addressFormGroup.get(addressControlName);
          addressControl?.setErrors({ required: true });
          addressControl?.markAsTouched();
        }
      }
    } else if (response.error.errorCode === 'ORG-005') {
      return this.handleError(response, 'organization.details.error.ORG-005');
    } else {
      return this.handleError(response, 'error.common');
    }
    this.isLoading = false;
    return EMPTY;
  }

  handleError(error: HttpErrorResponse, errorTranslationKey: string): Observable<never> {
    this.snackbarService.showErrorSnackbarWithTransloco(errorTranslationKey);
    this.isLoading = false;
    return EMPTY;
  }

  updateCountryChanged(): void {
    this.organizationForm.controls.address.markAsDirty();
  }

  changeTaxNumberRequired($event: boolean): void {
    if (!$event) {
      this.organizationForm.controls.taxNumber.disable();
      this.organizationForm.controls.taxNumber.removeValidators(Validators.required);
      this.organizationForm.controls.salesTaxId.enable();
      if (!this.isDemoOrganization) {
        this.organizationForm.controls.salesTaxId.addValidators(Validators.required);
      }
    } else {
      this.organizationForm.controls.taxNumber.enable();
      if (!this.isDemoOrganization) {
        this.organizationForm.controls.taxNumber.addValidators(Validators.required);
      }
      this.organizationForm.controls.salesTaxId.disable();
      this.organizationForm.controls.salesTaxId.removeValidators(Validators.required);
    }
  }

  addFurtherKin(value = ''): void {
    const furtherKinForm = new FormGroup({
      kin: new FormControl({ value, disabled: !this.isCurrentUserPlatformAdmin }, [
        Validators.required,
        Validators.pattern(RegexPattern.MASTER_KIN),
      ]),
    });
    this.furtherKins.push(furtherKinForm);
    this.organizationForm.markAsDirty();
  }

  removeFurtherKin(index: number): void {
    this.furtherKins.removeAt(index);
    this.organizationForm.markAsDirty();
  }

  private updateRequiredFields(details: OrganizationDetailData): void {
    if (details.billingEmail) {
      this.organizationForm.controls.billingEmail.addValidators(Validators.required);
    }
    if (details.masterKin && details.status === OrganizationStatus.ACTIVE) {
      this.organizationForm.controls.masterKin.addValidators(Validators.required);
    }

    if (details.markantId) {
      this.organizationForm.controls.markantId.addValidators(Validators.required);
    }

    if (this.organizationDetailData?.qualificationLevel === QualificationLevel.LEVEL_3) {
      this.organizationForm.controls.industry.addValidators(Validators.required);
      this.organizationForm.controls.organizationType.addValidators(Validators.required);
      this.organizationForm.controls.phone.addValidators(Validators.required);
      this.organizationForm.controls.address.controls.street.addValidators([Validators.required, notBlank]);
      this.organizationForm.controls.address.controls.streetNumber.addValidators([Validators.required, notBlank]);
      this.organizationForm.controls.address.controls.city.addValidators([Validators.required, notBlank]);
      this.organizationForm.controls.address.controls.countryCode.addValidators([Validators.required, notBlank]);
      this.organizationForm.controls.address.controls.zipCode.addValidators([Validators.required, notBlank]);
    }
  }

  private getOrganizationId(): Observable<string> {
    // The org id is only present for the navigation of a platform admin in the backoffice.
    // For roles like organization admin the id is not present due to the user has an active org session,
    // specified by the current orgId claim in the users JWT from our SSO.
    const organizationIdFromRoute = this.route.snapshot.paramMap.get('organizationId') as string;
    return organizationIdFromRoute ? of(organizationIdFromRoute) : this.authenticatedUserService.getOrganizationId();
  }

  private getRequesterRoles(): Observable<string[]> {
    return this.authenticatedUserService.getUserRoles().pipe(map(roles => roles.map(({ name }) => name)));
  }

  private activateForm(): void {
    if (this.organizationDetailData?.qualificationLevel === QualificationLevel.LEVEL_3) {
      this.organizationForm.controls.billingEmail.enable();
    }

    if (this.isCurrentUserPlatformAdmin) {
      this.organizationForm.controls.name.enable();
      this.organizationForm.controls.masterKin.enable();
      this.organizationForm.controls.industry.enable();
      this.organizationForm.controls.organizationType.enable();
      this.organizationForm.controls.gln.enable();
      this.organizationForm.controls.salesTaxNotAvailable.enable();
      this.organizationForm.controls.markantId.enable();

      if (!this.organizationDetailData?.markantOrganization) {
        this.organizationForm.controls.markantOrganization.enable();
      }

      if (!this.organizationDetailData?.salesTaxId?.trim().length) {
        this.organizationForm.get('taxNumber')?.enable();
      } else {
        this.organizationForm.get('salesTaxId')?.enable();
      }
    }
  }

  closeMarkantOrganizationChangeDialog(approved: boolean): void {
    this.openMarkantOrganizationChangeDialog = false;
    if (!approved) {
      this.organizationForm.controls.markantOrganization.setValue(false);
    }
  }

  openMarkantOrganizationChangeConfirmDialog(): void {
    if (this.organizationForm.controls.markantOrganization.value) {
      this.openMarkantOrganizationChangeDialog = true;
    }
  }

  saveUserWithReason(value: string): void {
    this.isAuditReasonDialogOpen = false;
    this.auditReason = value;
    this.onSubmit();
  }

  closeAuditReasonDialog(): void {
    this.isAuditReasonDialogOpen = false;
  }

  cancelForm(): void {
    this.router.navigate(['..'], { relativeTo: this.route });
  }
}
